import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { ordersAPI, packagesAPI } from '../../services/api';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Search, Filter, Download, Loader2, Trash2, ShieldCheck, History } from 'lucide-react';
import { toast } from 'sonner';
import { OrderTimelineSheet } from './OrderTimelineSheet';
import { fetchFiscalLabels, docInfo } from '../../lib/fiscalDocs';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [fiscalLabels, setFiscalLabels] = useState(null);
  // nome curto do documento do país (NIF, DNI...) — cabe na coluna
  const docLabel = (country) => {
    const l = docInfo(fiscalLabels, country)?.label || 'Documento';
    return l.length > 14 ? 'Documento' : l;
  };
  const [allOrders, setAllOrders] = useState([]); // Todos os pedidos do backend
  const [displayedOrders, setDisplayedOrders] = useState([]); // Pedidos exibidos (paginados)
  const [currentPage, setCurrentPage] = useState(1);
  const ORDERS_PER_PAGE = 1000; // Carregar 1000 por vez no frontend
  const [packages, setPackages] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentDate, setCurrentDate] = useState(null); // Data atual sendo exibida
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [checkingOrder, setCheckingOrder] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [e2eQuery, setE2eQuery] = useState('');
  const [e2eResult, setE2eResult] = useState(null);
  const [e2eLoading, setE2eLoading] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [timelineOrderId, setTimelineOrderId] = useState(null);

  const loadIntegrity = async () => {
    setIntegrityLoading(true);
    try {
      const response = await ordersAPI.paymentIntegrity(7);
      setIntegrity(response.data);
    } catch (error) {
      console.error('Erro na auditoria de integridade:', error);
    }
    setIntegrityLoading(false);
  };

  const investigateE2E = async () => {
    const e2e = e2eQuery.trim().replace(/\s/g, '');
    if (!e2e) { toast.error('Cole o E2E ID do comprovante'); return; }
    setE2eLoading(true);
    setE2eResult(null);
    try {
      const response = await ordersAPI.investigateE2E(e2e);
      setE2eResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao investigar E2E');
    }
    setE2eLoading(false);
  };

  const [filters, setFilters] = useState({
    txid: '',
    status: '',
    package_id: '',
    reseller_id: '',
    customer_cpf: '',
    start_date: '',
    end_date: '',
    search: '',
    payment_method: '',  // pix, mbway ou all
    pix_gateway: '',     // efi, stripe ou all
    source: ''           // chatbot, site ou all
  });

  // Campos de TEXTO são bufferizados: só aplicam ao clicar em "Aplicar Filtros" ou Enter
  // (evita busca/filtragem a cada tecla digitada)
  const [textDraft, setTextDraft] = useState({ search: '', txid: '', customer_cpf: '' });

  useEffect(() => {
    loadOrders();
    loadPackages();
    loadResellers();
    loadIntegrity();
    fetchFiscalLabels().then(setFiscalLabels);
  }, []);

  const loadPackages = async () => {
    try {
      const response = await packagesAPI.list(false);
      setPackages(response.data);
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    }
  };

  const loadResellers = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reseller/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResellers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar revendedores:', error);
    }
  };

  const loadOrders = async (specificDate = null, append = false, f = filters) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      if (!append) {
        setOrders([]);
        setCurrentDate(null);
      }
    }
    
    try {
      // IMPORTANTE: Usa horário do Brasil (UTC-3)
      let dateToLoad = specificDate;
      if (!dateToLoad && !append) {
        // Calcula data de hoje no horário do Brasil (UTC-3)
        const now = new Date();
        const utcTime = now.getTime();
        const brazilOffset = -3 * 60 * 60 * 1000; // -3 horas em milissegundos
        const brazilTime = new Date(utcTime + brazilOffset);
        dateToLoad = brazilTime.toISOString().split('T')[0];
        console.log('🇧🇷 Data Brasil calculada:', dateToLoad, '| Hora Brasil:', brazilTime.toISOString());
      } else if (append && currentDate) {
        // Se está fazendo append, carrega o dia anterior
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        dateToLoad = prevDate.toISOString().split('T')[0];
      }
      
      const params = { date: dateToLoad };
      if (f.status) params.status = f.status;
      if (f.customer_cpf) params.customer_cpf = f.customer_cpf.replace(/[.\-/\s]/g, '');
      if (f.payment_method) params.payment_method = f.payment_method;
      if (f.pix_gateway) params.pix_gateway = f.pix_gateway;
      if (f.source) params.source = f.source;
      
      const response = await ordersAPI.listByDate(params);
      const newOrders = response.data.orders;
      
      console.log(`📅 Carregando pedidos de ${dateToLoad}: ${newOrders.length} encontrados`);
      
      if (append) {
        setOrders([...orders, ...newOrders]);
      } else {
        setOrders(newOrders);
      }
      
      // Atualiza a data atual
      setCurrentDate(dateToLoad);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadPreviousDay = () => {
    loadOrders(null, true);
  };

  const handleApplyFilters = () => {
    // Aplica os campos de texto digitados (busca só acontece AQUI, não a cada tecla)
    const merged = { ...filters, ...textDraft };
    setFilters(merged);
    // Se tem filtro de data OU busca por TxID/identificador, usa o endpoint
    // geral (busca unificada no backend, em TODO o histórico — não só hoje)
    if (merged.start_date || merged.end_date || merged.txid) {
      loadOrdersByDateRange(merged);
    } else {
      loadOrders(null, false, merged);
    }
  };

  const loadOrdersByDateRange = async (f = filters) => {
    setLoading(true);
    setOrders([]);
    setAllOrders([]);
    setDisplayedOrders([]);
    setCurrentPage(1);
    
    try {
      const params = {};
      if (f.start_date) params.start_date = f.start_date;
      if (f.end_date) params.end_date = f.end_date;
      if (f.status) params.status = f.status;
      if (f.customer_cpf) params.customer_cpf = f.customer_cpf.replace(/[.\-/\s]/g, '');
      if (f.package_id) params.package_id = f.package_id;
      if (f.reseller_id) params.reseller_id = f.reseller_id;
      if (f.payment_method) params.payment_method = f.payment_method;
      if (f.pix_gateway) params.pix_gateway = f.pix_gateway;
      if (f.source) params.source = f.source;
      if (f.txid) params.txid = f.txid;  // busca unificada: TxID, TXN, MP ID ou E2E
      
      const response = await ordersAPI.list(params);
      const fetchedOrders = response.data;
      
      console.log(`📅 Pedidos carregados: ${fetchedOrders.length} | Exibindo primeiros ${Math.min(ORDERS_PER_PAGE, fetchedOrders.length)}`);
      
      // Armazena todos os pedidos
      setAllOrders(fetchedOrders);
      setOrders(fetchedOrders);
      
      // Exibe apenas os primeiros 1000
      setDisplayedOrders(fetchedOrders.slice(0, ORDERS_PER_PAGE));
      setCurrentDate(null); // Reseta paginação por dia
      
      if (fetchedOrders.length > ORDERS_PER_PAGE) {
        toast.info(`${fetchedOrders.length} pedidos encontrados. Mostrando primeiros ${ORDERS_PER_PAGE}. Role para carregar mais.`);
      }
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };
  
  const loadMoreOrders = () => {
    const nextPage = currentPage + 1;
    const startIndex = currentPage * ORDERS_PER_PAGE;
    const endIndex = startIndex + ORDERS_PER_PAGE;
    const moreOrders = allOrders.slice(startIndex, endIndex);
    
    if (moreOrders.length > 0) {
      setDisplayedOrders([...displayedOrders, ...moreOrders]);
      setCurrentPage(nextPage);
      console.log(`📄 Carregando página ${nextPage}: ${moreOrders.length} pedidos`);
    }
  };

  
  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Nenhum pedido selecionado');
      return;
    }

    // Abre dialog de confirmação
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await ordersAPI.deleteBulk(selectedOrders);
      toast.success(`${selectedOrders.length} pedido(s) excluído(s) com sucesso!`);
      setShowDeleteConfirm(false);
      setSelectedOrders([]);
      loadOrders();
    } catch (error) {
      toast.error('Erro ao excluir pedidos');
      setShowDeleteConfirm(false);
    }
  };


  const handleChangeStatus = async (orderId, newStatus) => {
    try {
      const response = await ordersAPI.updateStatus(orderId, newStatus);
      
      // Verifica se há warning de duplicação
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 5000 });
      } else {
        toast.success('Status atualizado com sucesso!');
      }
      
      // Atualiza apenas o pedido específico (sem reload total)
      setOrders(prevOrders => 
        prevOrders.map(o => 
          o.id === orderId ? { ...o, status: newStatus } : o
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleCheckStatus = async (orderId) => {
    setCheckingOrder(orderId);
    try {
      const response = await ordersAPI.checkStatus(orderId);
      const result = response.data;
      
      // Detectar gateway para exibir mensagem correta
      const order = orders.find(o => o.id === orderId);
      const gatewayLabel = order?.payment_method === 'mbway' ? 'MB WAY (Stripe)'
        : order?.pix_gateway === 'stripe' ? 'Stripe' 
        : order?.pix_gateway === 'mercadopago' ? 'Mercado Pago'
        : 'EFI';
      
      let message = `Verificação Completa:\n`;
      const pagoLabel = result.payment_check === 'indeterminado'
        ? '⚠️ NÃO FOI POSSÍVEL CONSULTAR (gateway não respondeu — não significa "não pago")'
        : result.payment_verified ? '✅ PAGO' : '❌ Não Pago';
      message += `• Pagamento ${gatewayLabel}: ${pagoLabel}\n`;
      message += `• Moedas KAKO: ${result.coins_credited ? '✅ Creditadas' : '❌ Não Creditadas'}\n`;
      if (result.actions_taken.length > 0) {
        message += `\nAções Executadas:\n${result.actions_taken.map(a => `• ${a}`).join('\n')}`;
      }
      
      (result.payment_check === 'indeterminado' ? toast.warning : toast.success)(message, { duration: 8000 });
      
      // Atualiza apenas o pedido específico (sem reload total)
      setOrders(prevOrders => 
        prevOrders.map(o => 
          o.id === orderId ? { ...o, status: result.new_status || o.status } : o
        )
      );
    } catch (error) {
      toast.error(error.response?.status === 503
        ? 'Banco de dados demorou para responder — tente novamente'
        : (error.response?.data?.detail || 'Erro ao verificar pedido'));
    } finally {
      setCheckingOrder(null);
    }
  };

  const [receiptOrder, setReceiptOrder] = useState(null);
  const [receiptE2E, setReceiptE2E] = useState('');
  const [receiptReason, setReceiptReason] = useState('');
  const [receiptForce, setReceiptForce] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const openReceiptDialog = (order) => {
    setReceiptOrder(order);
    setReceiptE2E(order.efi_webhook?.e2e_id || '');
    setReceiptReason('');
    setReceiptForce(false);
  };

  const handleConfirmByReceipt = async () => {
    if (!receiptOrder) return;
    setReceiptLoading(true);
    try {
      const payload = { e2e_id: receiptE2E.trim() };
      if (receiptForce) { payload.force = true; payload.reason = receiptReason.trim(); }
      const { data } = await ordersAPI.confirmByReceipt(receiptOrder.id, payload);
      (data.credited ? toast.success : toast.warning)(data.message, { duration: 8000 });
      setOrders(prev => prev.map(o => (o.id === receiptOrder.id ? { ...o, ...data.order } : o)));
      setReceiptOrder(null);
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = typeof d === 'object' && d !== null ? d.message : d;
      if (error.response?.status === 409 && typeof d === 'object' && d !== null && !receiptForce) {
        setReceiptForce(true);
        toast.warning(msg, { duration: 10000 });
      } else {
        toast.error(msg || 'Erro ao confirmar pelo comprovante');
      }
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleExportOrders = async () => {
    // Validar que período de datas está definido
    if (!filters.start_date || !filters.end_date) {
      toast.error('Defina o período (data início e fim) antes de exportar');
      return;
    }
    
    try {
      toast.info('Gerando planilha... Aguarde.');
      
      const token = localStorage.getItem('access_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Passar TODOS os filtros para exportação
      const params = new URLSearchParams();
      
      // Datas (obrigatórias)
      params.append('start_date', filters.start_date);
      params.append('end_date', filters.end_date);
      
      // Filtros opcionais
      if (filters.status && filters.status !== '') {
        params.append('status_filter', filters.status);
      }
      if (filters.package_id && filters.package_id !== '') {
        params.append('package_id', filters.package_id);
      }
      if (filters.reseller_id && filters.reseller_id !== '') {
        params.append('reseller_id', filters.reseller_id);
      }
      if (filters.customer_cpf && filters.customer_cpf !== '') {
        // pode ser CPF ou documento do exterior (com letras) — envia sem máscara
        params.append('customer_cpf', filters.customer_cpf.replace(/[.\-/\s]/g, ''));
      }
      if (filters.txid && filters.txid !== '') {
        params.append('txid', filters.txid);
      }
      
      console.log('📤 Exportando com filtros:', params.toString());
      
      // Faz requisição para download
      const response = await fetch(
        `${BACKEND_URL}/api/orders/export-excel?${params.toString()}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Erro ao exportar');
      }
      
      // Cria blob e faz download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos_${filters.start_date}_a_${filters.end_date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Planilha exportada com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar pedidos');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      paid: 'Pago',
      completed: 'Completo',
      failed: 'Falhou',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const filteredOrders = (displayedOrders.length > 0 ? displayedOrders : orders).filter(order => {
    // Filtro de revendedor
    if (filters.reseller_id && order.ref_reseller_id !== filters.reseller_id) {
      return false;
    }
    
    // Filtro de TxID / TXN / MP ID / E2E / Copia-e-cola (ID da transação)
    if (filters.txid) {
      const q = filters.txid.toLowerCase().trim();
      // Comprovante MP mostra "mpqrinter<id>", mas o pedido guarda só o número
      const qAlt = q.startsWith('mpqrinter') ? q.slice(9) : (q.startsWith('pix') ? q.slice(3) : '');
      const txHit = [order.txid, order.stripe_payment_intent_id, order.mp_payment_id, order.e2e_id, order.pix_copy_paste]
        .some(v => {
          if (!v) return false;
          const s = String(v).toLowerCase();
          return s.includes(q) || (qAlt && s.includes(qAlt));
        });
      if (!txHit) return false;
    }
    
    // Filtro de pacote
    if (filters.package_id && order.package_id !== filters.package_id) {
      return false;
    }
    
    // Filtro de data inicial
    if (filters.start_date) {
      const orderDate = order.created_at.split('T')[0];
      if (orderDate < filters.start_date) {
        return false;
      }
    }
    
    // Filtro de data final
    if (filters.end_date) {
      const orderDate = order.created_at.split('T')[0];
      if (orderDate > filters.end_date) {
        return false;
      }
    }
    
    // Filtro de busca
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        order.kako_id.toLowerCase().includes(search) ||
        order.kako_nickname?.toLowerCase().includes(search) ||
        order.id.toLowerCase().includes(search) ||
        order.txid?.toLowerCase().includes(search) ||
        order.stripe_payment_intent_id?.toLowerCase().includes(search) ||
        String(order.mp_payment_id || '').toLowerCase().includes(search) ||
        order.e2e_id?.toLowerCase().includes(search) ||
        order.pix_copy_paste?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="orders-management">
      <div>
        <h2 className="text-2xl font-bold">Histórico de Pedidos</h2>
        <p className="text-gray-600">Visualize e filtre todos os pedidos do sistema</p>
      </div>

      {/* Integridade de pagamentos — invariante "pedido pago nunca cancelado" */}
      <Card
        data-testid="payment-integrity-card"
        className={integrity && !integrity.healthy
          ? 'border-red-400 bg-red-50/60'
          : 'border-emerald-300 bg-emerald-50/40'}
      >
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Integridade de Pagamentos (7 dias)
            </span>
            <Button
              data-testid="payment-integrity-refresh"
              variant="outline"
              size="sm"
              onClick={loadIntegrity}
              disabled={integrityLoading}
            >
              {integrityLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reverificar'}
            </Button>
          </CardTitle>
          <CardDescription>
            Nenhum pedido pago pode ficar cancelado ou sem crédito. Cancelamento só acontece
            com o gateway confirmando que não houve pagamento.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!integrity ? (
            <p className="text-sm text-gray-600">Carregando auditoria...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="p-2 rounded-lg bg-white border" data-testid="integrity-violations">
                  <p className="text-xs text-gray-500">Pagos cancelados</p>
                  <p className={`text-lg font-bold ${integrity.counts.violations ? 'text-red-600' : 'text-emerald-600'}`}>
                    {integrity.counts.violations}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-white border" data-testid="integrity-paid-uncredited">
                  <p className="text-xs text-gray-500">Pagos sem crédito</p>
                  <p className={`text-lg font-bold ${integrity.counts.paid_uncredited ? 'text-red-600' : 'text-emerald-600'}`}>
                    {integrity.counts.paid_uncredited ?? 0}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-white border" data-testid="integrity-unverified">
                  <p className="text-xs text-gray-500">Aguardando gateway</p>
                  <p className="text-lg font-bold text-amber-600">{integrity.counts.unverified}</p>
                </div>
                <div className="p-2 rounded-lg bg-white border" data-testid="integrity-manual-review">
                  <p className="text-xs text-gray-500">Revisão manual</p>
                  <p className="text-lg font-bold text-amber-700">{integrity.counts.manual_review}</p>
                </div>
                <div className="p-2 rounded-lg bg-white border" data-testid="integrity-recovered">
                  <p className="text-xs text-gray-500">Recuperados autom.</p>
                  <p className="text-lg font-bold text-blue-600">{integrity.counts.recovered}</p>
                </div>
              </div>
              {integrity.healthy ? (
                <p className="text-sm text-emerald-800 font-medium" data-testid="integrity-ok">
                  ✅ Nenhum pedido pago sem crédito ou cancelado nos últimos {integrity.days} dias.
                </p>
              ) : (
                <div className="space-y-2" data-testid="integrity-violation-list">
                  {integrity.counts.violations > 0 && (
                    <p className="text-sm font-bold text-red-800">
                      🚨 {integrity.counts.violations} pedido(s) pago(s) com status cancelado — verifique agora:
                    </p>
                  )}
                  {[...(integrity.violations || []), ...(integrity.paid_uncredited || [])].map((o) => (
                    <div key={o.id} className="p-2 rounded-lg bg-white border border-red-300 text-sm flex items-center justify-between gap-2">
                      <span className="break-all">
                        <b>{o.kako_id}</b> {o.kako_nickname ? `(${o.kako_nickname})` : ''} · R$ {Number(o.amount || 0).toFixed(2)} · {o.coins} moedas
                        <span className="block text-xs text-gray-500">
                          {o.id} · status: {o.status}{o.error_message ? ` · ${o.error_message}` : ''}
                        </span>
                      </span>
                      <div className="flex flex-shrink-0 items-center gap-1">
                      <Button
                        size="sm"
                        data-testid={`integrity-check-${o.id}`}
                        onClick={async () => { await handleCheckStatus(o.id); loadIntegrity(); }}
                        disabled={checkingOrder === o.id}
                      >
                        {checkingOrder === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verificar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        title="Linha do tempo do pedido"
                        data-testid={`integrity-timeline-${o.id}`}
                        onClick={() => setTimelineOrderId(o.id)}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {(integrity.manual_review || []).length > 0 && (
                <div className="space-y-1" data-testid="integrity-manual-list">
                  <p className="text-sm font-bold text-amber-800">
                    ⏸️ Pedidos em revisão manual (ainda não concluídos):
                  </p>
                  {integrity.manual_review.map((o) => (
                    <p key={o.id} className="text-xs text-gray-700 break-all">
                      {o.id} · KAKO {o.kako_id} · status: {o.status}
                      {o.payment_check_failures
                        ? ` · ${o.payment_check_failures} consultas sem resposta do gateway`
                        : ' · crédito no KAKO ainda não confirmado'}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Investigação de comprovante por E2E */}
      <Card data-testid="e2e-investigation-card" className="border-amber-300 bg-amber-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            🔍 Investigar Comprovante (E2E)
          </CardTitle>
          <CardDescription>
            Cole o "ID da transação" do comprovante (E00...) — o sistema procura nas contas EFI e Mercado Pago e revela qual pedido recebeu o pagamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              data-testid="e2e-investigation-input"
              placeholder="Ex: E00416968202608262222mesvsxOjbCk"
              value={e2eQuery}
              onChange={(e) => setE2eQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') investigateE2E(); }}
            />
            <Button data-testid="e2e-investigation-button" onClick={investigateE2E} disabled={e2eLoading}>
              {e2eLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Investigar'}
            </Button>
          </div>
          {e2eResult && (
            <div className="text-sm space-y-2" data-testid="e2e-investigation-result">
              {e2eResult.efi?.found ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-300">
                  <p className="font-bold text-green-800">✅ Pagamento LOCALIZADO na conta EFI</p>
                  <p>Valor: <b>R$ {e2eResult.efi.valor}</b> · Horário: {e2eResult.efi.horario ? new Date(e2eResult.efi.horario).toLocaleString('pt-BR') : '-'}</p>
                  <p className="break-all">TxID do QR que recebeu: <b>{e2eResult.efi.txid || '-'}</b></p>
                  {e2eResult.efi.devolucoes?.length > 0 && (
                    <p className="text-red-700 font-bold">⚠️ Este PIX tem {e2eResult.efi.devolucoes.length} devolução(ões)!</p>
                  )}
                </div>
              ) : e2eResult.mp?.found ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-300">
                  <p className="font-bold text-green-800">✅ Pagamento LOCALIZADO na conta MERCADO PAGO</p>
                  <p>Valor: <b>R$ {e2eResult.mp.amount?.toFixed(2)}</b> · Status MP: <b>{e2eResult.mp.status}</b> · Aprovado: {e2eResult.mp.date_approved ? new Date(e2eResult.mp.date_approved).toLocaleString('pt-BR') : '-'}</p>
                  <p className="break-all">Payment ID: <b>{e2eResult.mp.payment_id}</b>{e2eResult.mp.payer_email ? ` · Pagador: ${e2eResult.mp.payer_email}` : ''}</p>
                </div>
              ) : (e2eResult.order || e2eResult.order_by_e2e) ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-300">
                  <p className="font-bold text-green-800">✅ E2E localizado em um pedido do sistema (veja abaixo)</p>
                  <p className="text-xs text-gray-600">Consulta direta nas contas EFI/MP não retornou — mas este E2E está registrado no pedido abaixo.</p>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-red-50 border border-red-300">
                  <p className="font-bold text-red-800">❌ E2E NÃO localizado na conta EFI{e2eResult.mp?.configured !== false ? ' nem no Mercado Pago' : ''}</p>
                  <p>
                    {e2eResult.mp?.configured === false
                      ? 'Mercado Pago não configurado neste ambiente — em produção a busca inclui o MP.'
                      : 'Este pagamento não entrou nas suas contas. Pode ser: conta de terceiros, Stripe, ou comprovante inválido.'}
                  </p>
                </div>
              )}
              {(e2eResult.order || e2eResult.order_by_e2e) ? (
                [e2eResult.order, e2eResult.order_by_e2e].filter(Boolean).map((o) => (
                  <div key={o.id} className="p-3 rounded-lg bg-blue-50 border border-blue-300">
                    <p className="font-bold text-blue-900">📦 Pedido dono deste pagamento:</p>
                    <p>KAKO ID: <b>{o.kako_id}</b> ({o.kako_nickname || '-'}) · Cliente: {o.customer_name || '-'}</p>
                    <p>Status: <Badge>{o.status}</Badge> · R$ {o.amount?.toFixed(2)} · {o.coins} moedas · {o.payment_method?.toUpperCase()}</p>
                    <p className="text-xs text-gray-600 break-all">Pedido: {o.id} · Criado: {o.created_at ? new Date(o.created_at).toLocaleString('pt-BR') : '-'}</p>
                  </div>
                ))
              ) : (
                (e2eResult.efi?.found || e2eResult.mp?.found) && (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-300">
                    <p className="font-bold text-yellow-800">⚠️ Pagamento recebido, mas NENHUM pedido corresponde</p>
                    <p>Pode ser um PIX avulso direto para a chave (fora do sistema de pedidos).</p>
                  </div>
                )
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card data-testid="orders-filters">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="search-input"
                  placeholder="ID, KAKO ID, Nickname..."
                  value={textDraft.search}
                  onChange={(e) => setTextDraft({ ...textDraft, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">ID da Transação</label>
              <Input
                data-testid="txid-filter"
                placeholder="TxID, TXN, MP ID ou E2E (comprovante)"
                value={textDraft.txid}
                onChange={(e) => setTextDraft({ ...textDraft, txid: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Documento do Cliente</label>
              <Input
                data-testid="cpf-filter"
                placeholder="CPF ou documento do exterior"
                value={textDraft.customer_cpf}
                onChange={(e) => setTextDraft({ ...textDraft, customer_cpf: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Início</label>
              <Input
                data-testid="start-date-filter"
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Fim</label>
              <Input
                data-testid="end-date-filter"
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="completed">Completo</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Pacote</label>
              <Select value={filters.package_id || "all"} onValueChange={(v) => setFilters({ ...filters, package_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="package-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Pacotes</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Revendedor</label>
              <Select value={filters.reseller_id || "all"} onValueChange={(v) => setFilters({ ...filters, reseller_id: v === "all" ? "" : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Revendedores</SelectItem>
                  {resellers.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.nickname || r.kako_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Método</label>
              <Select value={filters.payment_method || "all"} onValueChange={(v) => setFilters({ ...filters, payment_method: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="payment-method-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pix">💳 PIX</SelectItem>
                  <SelectItem value="mbway">📱 MB WAY</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Gateway</label>
              <Select value={filters.pix_gateway || "all"} onValueChange={(v) => setFilters({ ...filters, pix_gateway: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="gateway-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="efi">🟢 EFI</SelectItem>
                  <SelectItem value="stripe">🟣 Stripe</SelectItem>
                  <SelectItem value="mercadopago">🔵 Mercado Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Origem</label>
              <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ ...filters, source: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="source-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="chatbot">🤖 Vendas do Bot</SelectItem>
                  <SelectItem value="site">🌐 Site</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">&nbsp;</label>
              <Button data-testid="apply-filters-button" onClick={handleApplyFilters} className="w-full bg-purple-600 hover:bg-purple-700">
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Pedidos - Largura Total */}
      <Card className="w-screen" style={{maxWidth: '100vw', width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)'}}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle>{filteredOrders.length} Pedidos</CardTitle>
              {selectedOrders.length > 0 && (
                <Badge variant="destructive" data-testid="selected-count">
                  {selectedOrders.length} selecionado(s)
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedOrders.length > 0 && (
                <Button 
                  data-testid="delete-selected-button"
                  onClick={handleDeleteSelected} 
                  variant="destructive" 
                  size="sm"
                >
                  Excluir Selecionados
                </Button>
              )}
              <Button 
                data-testid="select-all-button"
                onClick={handleSelectAll} 
                variant="outline" 
                size="sm"
              >
                {selectedOrders.length === filteredOrders.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportOrders} data-testid="export-orders-button">
                <Download className="mr-2 h-4 w-4" />
                Exportar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b sticky top-0">
                <tr>
                  <th className="p-2 text-center">
                    <Checkbox
                      checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedOrders.includes(o.id))}
                      onCheckedChange={() => {
                        if (filteredOrders.every(o => selectedOrders.includes(o.id))) {
                          setSelectedOrders([]);
                        } else {
                          setSelectedOrders(filteredOrders.map(o => o.id));
                        }
                      }}
                    />
                  </th>
                  <th className="text-left p-2 font-semibold">ID</th>
                  <th className="text-left p-2 font-semibold">Data</th>
                  <th className="text-left p-2 font-semibold">Nickname</th>
                  <th className="text-left p-2 font-semibold">KAKO ID</th>
                  <th className="text-left p-2 font-semibold">Cliente</th>
                  <th className="text-left p-2 font-semibold">Documento</th>
                  <th className="text-left p-2 font-semibold">WhatsApp</th>
                  <th className="text-left p-2 font-semibold">Afiliado</th>
                  <th className="text-left p-2 font-semibold">Pacote</th>
                  <th className="text-right p-2 font-semibold">Valor</th>
                  <th className="text-center p-2 font-semibold">Método</th>
                  <th className="text-center p-2 font-semibold">Gateway</th>
                  <th className="text-left p-2 font-semibold">TxID/TXN</th>
                  <th className="text-center p-2 font-semibold">Status</th>
                  <th className="text-center p-2 font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="16" className="text-center py-8 text-gray-500">Nenhum pedido encontrado</td>
              </tr>
            ) : (
              filteredOrders.map((order) => (
                <tr key={order.id} className="border-b hover:bg-gray-50">
                  {/* Checkbox */}
                  <td className="p-2 text-center">
                    <Checkbox
                      checked={selectedOrders.includes(order.id)}
                      onCheckedChange={() => handleSelectOrder(order.id)}
                    />
                  </td>
                  
                  {/* ID */}
                  <td className="p-2 font-mono text-xs">#{order.id.slice(0, 8)}</td>
                  
                  {/* Data e Hora */}
                  <td className="p-2 text-xs">
                    <div>{new Date(order.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</div>
                    <div className="text-[10px] text-gray-500">{new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  
                  {/* Nickname KAKO */}
                  <td className="p-2 text-xs font-medium max-w-[120px] truncate">
                    {order.kako_nickname || '-'}
                  </td>
                  
                  {/* KAKO ID - Clicável */}
                  <td 
                    className="p-2 cursor-pointer hover:bg-purple-50 hover:text-purple-600 font-mono text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(order.kako_id);
                      toast.success('KAKO ID copiado!', { duration: 1500 });
                    }}
                    title="Clique para copiar"
                  >
                    {order.kako_id}
                  </td>
                  
                  {/* Cliente - Clicável */}
                  <td 
                    className="p-2 cursor-pointer hover:bg-purple-50 hover:text-purple-600"
                    onClick={() => {
                      navigator.clipboard.writeText(order.customer_name || order.kako_nickname);
                      toast.success('Nome copiado!', { duration: 1500 });
                    }}
                    title="Clique para copiar"
                  >
                    <div className="max-w-[120px] flex items-center gap-1">
                      {order.source === 'chatbot' && (
                        <span
                          data-testid="chatbot-order-badge"
                          title="Venda feita pelo Chatbot"
                          className="text-[10px] bg-orange-100 text-orange-700 px-1 rounded font-bold shrink-0"
                        >
                          🤖BOT
                        </span>
                      )}
                      <span className="truncate">{order.customer_name || order.kako_nickname}</span>
                    </div>
                  </td>
                  
                  {/* Documento (CPF no Brasil, documento fiscal no exterior) */}
                  <td
                    className="p-2 text-blue-600 font-mono text-xs cursor-pointer hover:bg-blue-50"
                    onClick={() => {
                      const doc = order.customer_cpf || order.customer_document;
                      if (doc) {
                        navigator.clipboard.writeText(doc);
                        toast.success('Documento copiado!', { duration: 1500 });
                      }
                    }}
                    title="Clique para copiar o documento"
                    data-testid={`order-document-${order.id?.slice(0, 8)}`}
                  >
                    {order.customer_cpf ? (
                      <>
                        {order.customer_cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                        <span className="block text-[10px] font-sans text-gray-400">CPF</span>
                      </>
                    ) : order.customer_document ? (
                      <>
                        {order.customer_document}
                        <span className="block text-[10px] font-sans text-gray-400">
                          {docLabel(order.customer_country)}
                          {order.customer_country ? ` · ${order.customer_country}` : ''}
                        </span>
                      </>
                    ) : '-'}
                  </td>
                  
                  {/* WhatsApp - Clicável */}
                  <td 
                    className="p-2 text-green-600 text-xs cursor-pointer hover:bg-green-50"
                    onClick={() => {
                      if (order.customer_whatsapp) {
                        navigator.clipboard.writeText(order.customer_whatsapp);
                        toast.success('WhatsApp copiado!', { duration: 1500 });
                      }
                    }}
                    title="Clique para copiar WhatsApp"
                  >
                    {order.source === 'chatbot' ? '🤖 via bot' : (order.customer_whatsapp || '-')}
                  </td>
                  
                  {/* Afiliado */}
                  <td 
                    className="p-2 text-purple-600 text-xs cursor-pointer hover:bg-purple-50"
                    onClick={() => {
                      if (order.ref_reseller_id) {
                        navigator.clipboard.writeText(order.ref_reseller_id);
                        toast.success('ID do Afiliado copiado!', { duration: 1500 });
                      }
                    }}
                    title="Clique para copiar ID do afiliado"
                  >
                    {order.ref_reseller_id ? (
                      resellers.find(r => r.id === order.ref_reseller_id)?.nickname || 
                      resellers.find(r => r.id === order.ref_reseller_id)?.kako_id?.slice(0, 8) || 
                      'Afiliado'
                    ) : '-'}
                  </td>
                  
                  {/* Pacote */}
                  <td className="p-2">
                    <div className="text-xs">{order.package_name}</div>
                    <div className="text-xs text-purple-600 font-bold">{order.coins?.toLocaleString()} 🪙</div>
                  </td>
                  
                  {/* Valor */}
                  <td className="p-2 text-right font-bold">
                    {order.payment_method === 'mbway' ? (
                      <div data-testid={`order-amount-eur-${order.id?.slice(0, 8)}`}>
                        <span className="text-indigo-600">
                          € {Number(order.mbway_amount_eur || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <div className="text-xs font-normal text-gray-500">
                          R$ {Number(order.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    ) : (
                      <span className="text-green-600">
                        R$ {Number(order.amount || 0).toFixed(2)}
                      </span>
                    )}
                  </td>
                  
                  {/* Método de Pagamento */}
                  <td className="p-2 text-center">
                    {order.payment_method === 'mbway' ? (
                      <Badge variant="outline" className="bg-indigo-100 text-indigo-800 border-indigo-300">
                        📱 MB WAY
                      </Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        💳 PIX
                      </Badge>
                    )}
                  </td>
                  
                  {/* Gateway */}
                  <td className="p-2 text-center">
                    {order.payment_method === 'mbway' ? (
                      <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300 text-xs">
                        Stripe
                      </Badge>
                    ) : order.pix_gateway === 'stripe' ? (
                      <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300 text-xs">
                        Stripe
                      </Badge>
                    ) : order.pix_gateway === 'mercadopago' ? (
                      <Badge variant="outline" className="bg-sky-100 text-sky-800 border-sky-300 text-xs">
                        Mercado Pago
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">
                        EFI
                      </Badge>
                    )}
                    {order.source === 'chatbot' && (
                      <Badge data-testid="bot-sale-badge" variant="outline" className="bg-cyan-100 text-cyan-800 border-cyan-300 text-xs mt-1 block w-fit mx-auto">
                        🤖 Bot
                      </Badge>
                    )}
                  </td>
                  
                  {/* TxID / TXN / E2E - Clicável */}
                  <td 
                    className="p-2 font-mono text-xs cursor-pointer hover:bg-blue-50 hover:text-blue-600"
                    onClick={() => {
                      const txValue = order.e2e_id || order.txid || order.stripe_payment_intent_id || order.mp_payment_id;
                      if (txValue) {
                        navigator.clipboard.writeText(String(txValue));
                        toast.success(order.e2e_id ? 'ID da transação (E2E) copiado!' : (order.stripe_payment_intent_id ? 'TXN copiado!' : 'TxID copiado!'), { duration: 1500 });
                      }
                    }}
                    title={order.e2e_id ? 'Clique para copiar ID da transação (E2E)' : (order.stripe_payment_intent_id ? 'Clique para copiar TXN (Stripe)' : 'Clique para copiar TxID')}
                  >
                    <div className="max-w-[100px] truncate">
                      {order.e2e_id ? (
                        <span className="text-emerald-600" data-testid={`order-e2e-${order.id}`}>{order.e2e_id.slice(0, 14)}...</span>
                      ) : order.stripe_payment_intent_id ? (
                        <span className="text-purple-600">{order.stripe_payment_intent_id.slice(0, 14)}...</span>
                      ) : order.txid ? (
                        <span>{order.txid.slice(0, 12)}...</span>
                      ) : order.mp_payment_id ? (
                        <span className="text-sky-600">MP {String(order.mp_payment_id).slice(0, 12)}</span>
                      ) : '-'}
                    </div>
                  </td>
                  
                  {/* Status - Dropdown */}
                  <td className="p-2">
                    {order.fraud_suspect && (
                      <Badge
                        data-testid={`fraud-suspect-badge-${order.id}`}
                        title={`Suspeita de fraude sinalizada pelo gateway${order.fraud_suspect_note ? `: ${order.fraud_suspect_note}` : ''} · ninguém foi bloqueado`}
                        className="mb-1 block w-fit bg-amber-100 text-amber-900 border border-amber-300 text-[10px]">
                        ⚠ Suspeita
                      </Badge>
                    )}
                    <Select 
                      value={order.status} 
                      onValueChange={(newStatus) => handleChangeStatus(order.id, newStatus)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <Badge className={getStatusColor(order.status)}>
                          {getStatusLabel(order.status)}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                        <SelectItem value="completed">Completo</SelectItem>
                        <SelectItem value="failed">Falhou</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  
                  {/* Ações - Checar / Linha do tempo */}
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCheckStatus(order.id)}
                        disabled={checkingOrder === order.id || !order.txid}
                        className="h-7 text-xs"
                      >
                        {checkingOrder === order.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          '🔍 Checar'
                        )}
                      </Button>
                      {order.txid && !['paid', 'completed'].includes(order.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          title="Confirmar pagamento pelo E2E do comprovante do cliente"
                          data-testid={`order-receipt-${order.id}`}
                          onClick={() => openReceiptDialog(order)}
                          className={`h-7 px-2 text-xs ${order.efi_webhook ? 'border-amber-400 text-amber-700' : ''}`}
                        >
                          🧾
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        title="Linha do tempo do pedido"
                        data-testid={`order-timeline-${order.id}`}
                        onClick={() => setTimelineOrderId(order.id)}
                        className="h-7 px-2"
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
              </tbody>
            </table>
            
            {/* Botão Carregar Mais */}
            {allOrders.length > displayedOrders.length && (
              <div className="mt-4 text-center">
                <Button
                  onClick={loadMoreOrders}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Carregar Mais ({displayedOrders.length} de {allOrders.length} pedidos)
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Carregando {ORDERS_PER_PAGE} pedidos por vez para melhor performance
                </p>
              </div>
            )}
          </div>
          
          {/* Botão para carregar dia anterior */}
          <div className="p-4 border-t bg-gray-50 flex justify-center">
            <Button
              variant="outline"
              onClick={handleLoadPreviousDay}
              disabled={loadingMore}
              className="w-full md:w-auto"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  📅 Carregar pedidos do dia anterior
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão de Pedidos</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir <strong>{selectedOrders.length} pedido(s)</strong> selecionado(s)?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação não pode ser desfeita. Os pedidos serão removidos permanentemente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!receiptOrder} onOpenChange={(o) => { if (!o) setReceiptOrder(null); }}>
        <DialogContent data-testid="receipt-confirm-dialog">
          <DialogHeader>
            <DialogTitle>🧾 Confirmar pagamento pelo comprovante</DialogTitle>
          </DialogHeader>
          {receiptOrder && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-600">
                Pedido <strong>#{receiptOrder.id.slice(0, 8)}</strong> · R$ {Number(receiptOrder.amount || 0).toFixed(2)} ·{' '}
                {receiptOrder.kako_nickname || receiptOrder.kako_id} · status <strong>{receiptOrder.status}</strong>
              </p>
              {receiptOrder.efi_webhook && (
                <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="receipt-webhook-evidence">
                  A EFI avisou este pagamento por webhook em {receiptOrder.efi_webhook.received_at?.slice(0, 16).replace('T', ' ')}
                  {' '}(E2E {receiptOrder.efi_webhook.e2e_id}, R$ {receiptOrder.efi_webhook.valor}).
                </p>
              )}
              <div>
                <label className="text-xs font-medium text-gray-700">E2E ID do comprovante (ID da transação Pix)</label>
                <Input
                  data-testid="receipt-e2e-input"
                  value={receiptE2E}
                  onChange={(e) => setReceiptE2E(e.target.value)}
                  placeholder="E08561701202611030305..."
                  className="font-mono"
                />
              </div>
              {receiptForce && (
                <div className="space-y-1 rounded border border-red-300 bg-red-50 p-2">
                  <p className="text-xs text-red-700">
                    A EFI não confirmou este E2E. Só force se o dinheiro está visível no extrato EFI. Esta ação é auditada e alertada.
                  </p>
                  <Input
                    data-testid="receipt-force-reason"
                    value={receiptReason}
                    onChange={(e) => setReceiptReason(e.target.value)}
                    placeholder="Motivo (ex: conferido no extrato EFI às 00:10, R$ 6,00 de UNDIARA)"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOrder(null)}>Cancelar</Button>
            <Button
              data-testid="receipt-confirm-button"
              variant={receiptForce ? 'destructive' : 'default'}
              onClick={handleConfirmByReceipt}
              disabled={receiptLoading || receiptE2E.trim().length < 32 || (receiptForce && receiptReason.trim().length < 10)}
            >
              {receiptLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : receiptForce ? 'Forçar confirmação' : 'Confirmar e creditar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OrderTimelineSheet
        orderId={timelineOrderId}
        onClose={() => setTimelineOrderId(null)}
      />
    </div>
  );
};

export default OrdersManagement;
